import React,{useEffect,useRef,useState} from 'react';
import {Link,useLocation,useNavigate} from 'react-router-dom';
import {useApp} from '../contexts/AppContext';
import * as Fi from 'react-icons/fi';


import {clearSession,getStoredSession,getSheetConnection} from '../lib/bpaAuth.js';
import {dbService} from '../services/dbService.js';
import {getSheetId,rememberSheetId,getBaseline,getLocalManifest,isGoogleAuthorized,authorizeGoogle,uploadSnapshot,latestRevision,downloadSnapshot,markRestored,localMatchesTrustedBaseline} from '../lib/expenseGoogleSheets.js';
import {localDirtyMarker,acknowledgeLocalDirty,isPristineCustomerWorkspace,withWorkspaceWriteLock} from '../lib/expenseAutoSyncState.js';
import {searchWorkspace} from '../services/smartSearch.js';
import {DEVICE_ONLY_SETTINGS} from '../lib/workspaceSchema.js';

// Consolidated from src/hooks/useInstallApp.js; keep its behavior intact.
const useInstallApp = (()=>{

let deferredPrompt=null;
const subscribers=new Set();
const isStandalone=()=>typeof window!=='undefined'&&
  (window.matchMedia?.('(display-mode: standalone)').matches||window.navigator.standalone===true);
const isIOS=()=>typeof navigator!=='undefined'&&
  (/iPad|iPhone|iPod/.test(navigator.userAgent)||(navigator.platform==='MacIntel'&&navigator.maxTouchPoints>1));
let installed=typeof window!=='undefined'&&isStandalone();
const notify=()=>subscribers.forEach(callback=>callback());
if(typeof window!=='undefined'){
  window.addEventListener('beforeinstallprompt',event=>{
    event.preventDefault();deferredPrompt=event;notify();
  });
  window.addEventListener('appinstalled',()=>{installed=true;deferredPrompt=null;notify()});
}
function useInstallApp(){
  const [,setVersion]=useState(0);
  const [guidance,setGuidance]=useState(false);
  useEffect(()=>{
    const callback=()=>setVersion(n=>n+1);
    subscribers.add(callback);
    const onDisplayChange=()=>{installed=isStandalone()||installed;notify()};
    const media=window.matchMedia?.('(display-mode: standalone)');
    media?.addEventListener?.('change',onDisplayChange);
    return()=>{subscribers.delete(callback);media?.removeEventListener?.('change',onDisplayChange)};
  },[]);
  const alreadyInstalled=installed||isStandalone();
  const ios=isIOS();
  const available=!alreadyInstalled&&(ios||Boolean(deferredPrompt));
  const install=async()=>{
    if(alreadyInstalled)return;
    if(ios){setGuidance(true);return}
    const prompt=deferredPrompt;if(!prompt)return;
    deferredPrompt=null;notify();
    try{
      await prompt.prompt();
      const choice=await prompt.userChoice;
      if(choice?.outcome==='accepted'){installed=true;setGuidance(false)}
    }catch(error){console.warn('Install prompt unavailable:',error)}
    finally{notify()}
  };
  return {available,alreadyInstalled,ios,install,guidance,setGuidance};
}

return useInstallApp;
})();

// Consolidated from src/components/InstallAppButton.jsx; keep its behavior intact.
const InstallAppButton = (()=>{



function InstallAppButton(){
 const {available,ios,install,guidance,setGuidance}=useInstallApp();
 const closeRef=useRef(null);
 useEffect(()=>{if(!guidance)return;closeRef.current?.focus();const onKey=e=>{if(e.key==='Escape')setGuidance(false)};document.addEventListener('keydown',onKey);return()=>document.removeEventListener('keydown',onKey)},[guidance,setGuidance]);
 if(!available)return null;
 return <>
 <button type="button" onClick={install} aria-label="Install Expense Manager Pro" title="Install Expense Manager Pro" className="install-app-button relative rounded-xl h-10 px-2 sm:px-3 shrink-0 inline-flex items-center justify-center gap-2 text-xs font-bold focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600">
 <Fi.FiDownload size={17}/><span className="hidden sm:inline">Install App</span></button>
 {guidance&&ios&&<div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-slate-900/55 backdrop-blur-[2px] p-0 sm:p-5" onMouseDown={e=>{if(e.target===e.currentTarget)setGuidance(false)}}>
 <section role="dialog" aria-modal="true" aria-labelledby="install-heading" className="w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl px-5 pt-5 pb-6 text-slate-900" style={{paddingBottom:'max(24px,env(safe-area-inset-bottom))'}}>
 <div className="flex justify-between gap-3 items-start mb-5"><div className="flex gap-3 items-center"><span className="w-12 h-12 bg-blue-50 text-blue-600 grid place-items-center rounded-xl"><Fi.FiSmartphone size={22}/></span><div><h2 id="install-heading" className="text-lg font-bold">Install Expense Manager Pro</h2><p className="text-xs text-slate-500">Add to your Home Screen</p></div></div><button ref={closeRef} onClick={()=>setGuidance(false)} aria-label="Close installation instructions" className="w-10 h-10 rounded-lg grid place-items-center hover:bg-slate-100"><Fi.FiX size={21}/></button></div>
 <p className="text-sm text-slate-600 mb-5">Launch your expense manager directly from your Home Screen in an app-like view.</p>
 <ol className="space-y-4">{[['1','Tap Share',<Fi.FiShare size={19}/>],['2','Choose Add to Home Screen',<Fi.FiPlusSquare size={19}/>],['3','Tap Add',<Fi.FiCheck size={19}/>]].map(([n,label,icon])=><li key={n} className="flex items-center gap-3"><span className="h-9 w-9 rounded-full bg-blue-50 text-blue-600 font-bold grid place-items-center">{n}</span><span className="text-sm font-semibold flex-1">{label}</span><span className="text-blue-600">{icon}</span></li>)}</ol>
 <p className="text-xs text-slate-500 mt-5">In Safari, Share is in the browser toolbar. If you are using another browser, open this page in Safari for the familiar Add to Home Screen steps.</p>
 <button onClick={()=>setGuidance(false)} className="mt-5 w-full h-11 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold">Got it</button>
 </section></div>}</>;
}

return InstallAppButton;
})();

// Consolidated from src/components/GoogleAutoSyncStatus.jsx; keep its behavior intact.
const GoogleAutoSyncStatus = (()=>{







const DEBOUNCE_MS=1400;
const MIN_UPLOAD_INTERVAL_MS=30000; // Preserve existing verified upload coalescing.
const REMOTE_POLL_MS=300000; // Five minutes; no per-edit Google reads.
const MIN_RECHECK_MS=90000; // Prevent repeated foreground-triggered quota consumption.
const INITIAL={kind:'checking',label:'Checking backup',message:''};

// Runs on every authenticated finance page, not only while Settings is open.
// Never creates workbooks, stores Google tokens or modifies local financial data.
// A local write is considered backed up only after the existing verified
// append/revision/checksum workflow completes.
function GoogleAutoSyncStatus(){
 const {actions}=useApp();
 const [status,setStatus]=useState(INITIAL);
 const [reconnectBusy,setReconnectBusy]=useState(false);
 const [reconnectError,setReconnectError]=useState('');
 const control=useRef({timer:null,busy:false,paused:false,disposed:false,lastUploadAt:0,lastRemoteCheckAt:0,registeredSheet:'',lastRegistryAt:0});
 useEffect(()=>{
   const worker=control.current;
   worker.disposed=false;
   const userId=dbService.getWorkspaceUser();
   if(!userId){setStatus({kind:'error',label:'No BPA workspace',message:'Sign in again.'});return undefined;}
   const sameCustomer=()=>!worker.disposed&&dbService.getWorkspaceUser()===userId&&
     getStoredSession()?.record?.id===userId;
   const report=(kind,label,message='')=>{
     if(sameCustomer())setStatus({kind,label,message});
   };
   const schedule=(force=false,delay=DEBOUNCE_MS)=>{
     if(worker.disposed)return;
     if(worker.timer)clearTimeout(worker.timer);
     const remaining=Math.max(0,worker.lastUploadAt+MIN_UPLOAD_INTERVAL_MS-Date.now());
     worker.timer=setTimeout(()=>{worker.timer=null;void run(force)},Math.max(delay,remaining));
   };
   const hold=(label,message)=>{
     worker.paused=true;report('warning',label,message);
   };
   async function run(force=false){
     if(worker.disposed||worker.busy)return;
     const session=getStoredSession();
     if(!session?.token||session.record?.id!==userId){
       report('paused','BPA sign-in needed','Sign in to the correct customer account before syncing.');
       return;
     }
     if(navigator.onLine===false){
       report('paused','Offline · saved locally','Google checks resume when this browser comes online.');
       return;
     }
     // Never infer ownership from a locally cached Sheet ID. Discover the
     // customer-entitled workbook from BPA Applications Hub, including on a
     // completely new device where no spreadsheet ID is cached yet.
     worker.busy=true;
     let operation='discovery';
     try{
       let sheet=getSheetId();
       if(!worker.registeredSheet||worker.registeredSheet!==sheet||
          Date.now()-worker.lastRegistryAt>=REMOTE_POLL_MS){
         const registered=await getSheetConnection(session.token);
         if(!sameCustomer())throw new Error('BPA customer changed during workbook discovery.');
         if(!registered?.spreadsheetId){
           report('setup','Connect Google Sheets','No verified workbook is registered for this BPA customer. Open Settings to complete setup.');
           return;
         }
         if(sheet&&sheet!==registered.spreadsheetId){
           hold('Workbook mismatch','The saved workbook differs from your BPA customer registration. No records were changed. Review Google setup in Settings.');
           return;
         }
         if(!sheet){rememberSheetId(registered.spreadsheetId);sheet=registered.spreadsheetId;}
         worker.registeredSheet=sheet;
         worker.lastRegistryAt=Date.now();
       }
       if(!sheet){
         report('setup','Connect Google Sheets','Open Settings to link your existing customer workbook.');
         return;
       }
       if(!isGoogleAuthorized()){
         report('paused','Google reconnect needed','Your existing workbook was found. Authorise Google in this browser to load and back up records safely.');
         return;
       }
       const baseline=getBaseline(sheet);
       const marker=localDirtyMarker(userId);
       if(worker.paused&&!force)return;
       // The default categories are seeded locally on first login. They must
       // not be mistaken for unsynced customer records and uploaded over the
       // established financial history on another device.
       const local=await dbService.exportData();
       const pristine=isPristineCustomerWorkspace(local);
       // Never treat a previously linked, now-empty database as an untouched
       // new device. It might reflect intentional local deletions or a partial
       // browser-storage reset; either case requires customer review.
       if(baseline&&pristine&&(marker||!await localMatchesTrustedBaseline(sheet,local))){
         hold('Local recovery needs review','This device has an older verified revision but its local records now resemble an empty workspace. Check Sheet before any replacement or upload.');
         return;
       }
       if(marker&&!pristine){
         if(!baseline||!getLocalManifest(sheet)){
           hold('Verify local records','This device has changes but no trusted Google baseline manifest. Use Check Sheet before uploading.');
           return;
         }
         if(!sameCustomer()||getSheetId()!==sheet)throw new Error('Customer workbook changed before backup.');
         report('syncing','Syncing to Google','Saving and verifying your local financial changes…');
         operation='upload';
         const uploaded=await uploadSnapshot(sheet,local);
         worker.lastUploadAt=Date.now();
         if(!sameCustomer())throw new Error('Customer changed while backing up. Check Sheet before retrying.');
         const acknowledged=acknowledgeLocalDirty(userId,marker);
         window.dispatchEvent(new CustomEvent('bpa:google-autosynced',
           {detail:{userId,sheetId:sheet,revision:uploaded.revision,counts:uploaded.counts}}));
         worker.paused=false;
         if(!acknowledged&&localDirtyMarker(userId))
           report('queued','More edits pending','A newer local edit will be included in the next verified backup.');
         else report(uploaded.formattingWarning?'warning':'synced',
           uploaded.formattingWarning?'Backup saved; formatting needs repair':uploaded.unchanged?'Google up to date':'Backed up to Google',
           uploaded.formattingWarning||'Verified revision '+uploaded.revision+'. Your local records remain available.');
         return;
       }
       if(!baseline&&!pristine){
         hold('Review existing local data','This browser already contains records that have not been linked to the verified Google revision. Use Check Sheet; no automatic restore or overwrite occurred.');
         return;
       }
       // A remote download runs only on login, after reauthorisation,
       // approximately every five minutes, or on a sufficiently spaced return
       // to the foreground. This avoids the historical Sheets read-quota issue.
       const elapsed=Date.now()-worker.lastRemoteCheckAt;
       if(worker.lastRemoteCheckAt&&elapsed<(force?MIN_RECHECK_MS:REMOTE_POLL_MS))return;
       worker.lastRemoteCheckAt=Date.now();
       const baselineMatches=baseline?await localMatchesTrustedBaseline(sheet,local):false;
       if(!pristine&&!baselineMatches){
         hold('Local changes need review','This device differs from its last verified cloud backup, even though no pending-edit marker was found. Nothing was replaced.');
         return;
       }
       report('syncing','Checking Google updates','Checking the latest verified financial revision…');
       operation='read';
       const head=await latestRevision(sheet);
       if(!head){
         report('setup','Initial backup needed','This workbook has no completed financial backup. Finish Google setup in Settings.');
         return;
       }
       if(baseline===head.revision && baselineMatches){
         report('synced','Google backup ready','This device matches Google revision '+head.revision+'.');
         return;
       }
       if(document.querySelector('[role="dialog"],[aria-modal="true"]')||
          document.activeElement?.matches?.('input,textarea,select,[contenteditable="true"]')){
         report('queued','Newer backup available','Finish your current form before the app safely refreshes financial records.');
         return;
       }
       report('syncing','Loading latest records','Downloading and validating the latest cloud records before making any local changes…');
       const remote=await downloadSnapshot(sheet);
       if(remote.revision!==head.revision)throw new Error('Google revision changed during download; local data was preserved.');
       const startMarker=localDirtyMarker(userId);
       if(startMarker!==marker)throw new Error('Local records changed during download. Automatic refresh stopped to prevent overwriting them.');
       const savedBaseline=baseline;
       operation='import';
       await withWorkspaceWriteLock(userId,async()=>{
         if(!sameCustomer()||getSheetId()!==sheet||getBaseline(sheet)!==savedBaseline)
           throw new Error('Account or trusted revision changed during refresh. No local records were replaced.');
         if(localDirtyMarker(userId)!==startMarker)
           throw new Error('Another tab changed local records during cloud refresh. Nothing was replaced.');
         const current=await dbService.exportData();
         if(pristine){
           if(!isPristineCustomerWorkspace(current))
             throw new Error('Customer data appeared locally during recovery. Automatic replacement stopped.');
         }else if(!await localMatchesTrustedBaseline(sheet,current))
           throw new Error('The local database no longer matches its trusted baseline. Automatic replacement stopped.');
         // Only a final confirmed head may be installed; Google writes are
         // optimistic across separate devices and can still race after this
         // read, so the next poll rechecks rather than ever overwriting Google.
         const confirmed=await latestRevision(sheet);
         if(confirmed?.revision!==head.revision||confirmed?.checksum!==head.checksum)
           throw new Error('A newer Google revision appeared before recovery. Retry safely.');
         if(!sameCustomer()||localDirtyMarker(userId)!==startMarker)
           throw new Error('The customer session or local records changed before import.');
         const localSettings=current.data.settings.filter(x=>DEVICE_ONLY_SETTINGS.includes(x.key));
         const replacement={...remote,data:{...remote.data,settings:[
           ...remote.data.settings.filter(x=>!DEVICE_ONLY_SETTINGS.includes(x.key)),...localSettings
         ]}};
         // One IndexedDB transaction restores ALL finance datasets together.
         // If it fails, the prior database rolls back.
         await dbService.importData(replacement);
         await markRestored(sheet,remote.revision,replacement);
         if(startMarker)acknowledgeLocalDirty(userId,startMarker);
       },{required:true});
       if(!sameCustomer())throw new Error('Customer session changed after local recovery.');
       await actions.refreshFromDatabase();
       worker.paused=false;
       window.dispatchEvent(new CustomEvent('bpa:google-autosynced',
         {detail:{userId,sheetId:sheet,revision:remote.revision,counts:remote.counts}}));
       report('synced','Updated from Google','Verified revision '+remote.revision+' was safely loaded to this device. No remote records were overwritten.');
     }catch(error){
       const message=error?.message||'Automatic refresh stopped without deliberately overwriting any cloud records.';
       if(operation==='read'&&error?.code==='GOOGLE_READ_QUOTA'){
         // Only reattempt a rate-limited READ on the next ordinary poll.
         // Ambiguous writes always remain paused for explicit review.
         worker.paused=false;
         report('warning','Google read limit',"Automatic checks will resume after Google's read quota cools down. Local data was not changed.");
       }else{
         worker.paused=true;
         report('error','Sync needs attention',message);
       }
     }finally{
       worker.busy=false;
       if(!worker.paused&&localDirtyMarker(userId))schedule(false);
     }
   }
   const mutation=event=>{
     if(event.detail?.userId!==userId)return;
     if(!worker.paused){
       report('queued','Saved locally · backing up','Google backup starts after nearby edits have settled.');
       schedule(false);
     }
   };
   const ready=()=>{
     if(!isGoogleAuthorized()){
       report('paused','Google reconnect needed','Reconnect Google to resume verified backups. Local records remain safe.');
       return;
     }
     worker.paused=false;
     worker.lastRemoteCheckAt=0; // Explicit permission renewal triggers a safe comparison.
     schedule(true,100);
   };
   const storage=event=>{
     if(event.key==='bpa-expense-google-dirty-v1:'+userId&&!worker.paused)schedule(false);
     if(event.key?.startsWith('bpa-expense-sync-base:')&&event.key.includes(':'+userId+':')&&!worker.paused)
       schedule(true,250);
   };
   const online=()=>{worker.paused=false;schedule(true,200)};
   const visible=()=>{
     if(document.visibilityState==='visible'&&!worker.paused)schedule(true,300);
   };
   const poll=setInterval(()=>{
     if(document.visibilityState==='visible'&&navigator.onLine!==false&&!worker.paused)
       schedule(false,300);
   },REMOTE_POLL_MS);
   window.addEventListener('bpa:finance-mutated',mutation);
   window.addEventListener('bpa:google-ready',ready);
   window.addEventListener('storage',storage);
   window.addEventListener('online',online);
   document.addEventListener('visibilitychange',visible);
   schedule(false,120);
   return ()=>{
     worker.disposed=true;
     if(worker.timer)clearTimeout(worker.timer);
     clearInterval(poll);
     window.removeEventListener('bpa:finance-mutated',mutation);
     window.removeEventListener('bpa:google-ready',ready);
     window.removeEventListener('storage',storage);
     window.removeEventListener('online',online);
     document.removeEventListener('visibilitychange',visible);
   };
 },[]);
 // Google Identity Services requires a user gesture to renew an expired token.
 // This only requests permission for the already-linked customer's Google
 // account. Uploads still use the existing verified PocketBase/Sheet guard.
 const reconnectGoogle=async()=>{
   if(reconnectBusy)return;
   const owner=dbService.getWorkspaceUser();
   const session=getStoredSession();
   const sheet=getSheetId();
   if(!owner||!session?.token||session.record?.id!==owner){
     setReconnectError('Your BPA customer session needs verification. Sign in again before reconnecting Google.');
     return;
   }
   if(!sheet){
     setReconnectError('No customer workbook was found. Open Google setup in Settings to link it first.');
     return;
   }
   setReconnectBusy(true);setReconnectError('');
   try{
     await authorizeGoogle();
     if(dbService.getWorkspaceUser()!==owner||getStoredSession()?.record?.id!==owner)
       throw new Error('BPA account changed while reconnecting. Sign in again to continue.');
     if(!isGoogleAuthorized())
       throw new Error('Google did not return valid permission. Please reconnect again.');
     window.dispatchEvent(new Event('bpa:google-ready'));
   }catch(error){
     setReconnectError(error?.message||'Google could not be reconnected. Open Settings to retry.');
   }finally{setReconnectBusy(false)}
 };
 const icon=status.kind==='synced'?<Fi.FiCheckCircle className="text-emerald-600"/>:
   status.kind==='syncing'?<Fi.FiRefreshCw className="animate-spin text-blue-600"/>:
   status.kind==='error'?<Fi.FiAlertCircle className="text-rose-600"/>:
   status.kind==='warning'?<Fi.FiAlertTriangle className="text-amber-600"/>:
   <Fi.FiCloud className="text-slate-500"/>;
 const warning=['error','paused','warning','setup'].includes(status.kind);
 return <div className="flex shrink-0 items-center gap-1" aria-live="polite" aria-label="Google Sheets automatic sync status">
   <Link to="/settings" title={status.message||status.label}
     className={'inline-flex items-center gap-2 rounded-lg px-2 py-2 text-xs font-semibold border transition-colors cursor-pointer hover:shadow-sm focus-visible:ring-2 focus-visible:ring-blue-500 '+
       (warning?'text-amber-900 bg-amber-50 border-amber-200':status.kind==='synced'?
         'text-emerald-900 bg-emerald-50 border-emerald-200':'text-blue-800 bg-blue-50 border-blue-200')}>
     {icon}<span className="hidden xl:inline">{status.label}</span>
     <span className="sr-only">. Open Settings for Google Sheets details.</span>
   </Link>
   {status.kind==='paused'&&status.label==='Google reconnect needed'&&
     <button type="button" disabled={reconnectBusy} aria-label="Reconnect Google Sheets"
       title="Reconnect permission for the existing customer-owned Google Sheet"
       onClick={reconnectGoogle}
       className="inline-flex min-h-9 min-w-9 shrink-0 items-center justify-center gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-2 text-xs font-bold text-amber-900 hover:bg-amber-100 focus-visible:ring-2 focus-visible:ring-amber-400 disabled:cursor-wait disabled:opacity-60">
       {reconnectBusy?<Fi.FiLoader className="animate-spin" size={16}/>:<Fi.FiRefreshCw size={16}/>}
       <span className="hidden 2xl:inline">{reconnectBusy?'Connecting…':'Reconnect'}</span>
     </button>}
   {status.kind==='error'&&<button type="button" title="Retry Google backup without changing local records"
     onClick={()=>window.dispatchEvent(new Event('bpa:google-ready'))}
     className="rounded-lg border border-amber-200 p-2 text-amber-900 hover:bg-amber-50" aria-label="Retry Google backup"><Fi.FiRotateCw/></button>}
   {reconnectError&&<div role="alert"
     className="fixed right-3 top-[76px] z-[99] flex max-w-[min(92vw,430px)] items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-3 text-xs font-semibold leading-relaxed text-amber-900 shadow-lg">
     <Fi.FiAlertTriangle className="mt-0.5 shrink-0" size={17}/>
     <span className="min-w-0 flex-1">{reconnectError} <Link className="underline underline-offset-2" to="/settings" onClick={()=>setReconnectError('')}>Google Settings</Link></span>
     <button type="button" aria-label="Dismiss Google reconnect error" onClick={()=>setReconnectError('')}
       className="grid h-7 w-7 shrink-0 place-items-center rounded-lg hover:bg-amber-100"><Fi.FiX size={16}/></button>
   </div>}
 </div>;
}

return GoogleAutoSyncStatus;
})();

const pages={'/':'Dashboard','/transactions':'Transactions','/recurring':'Recurring','/budgets':'Budgets','/categories':'Categories','/people':'People','/reports':'Reports','/settings':'Settings'};
function WorkspaceSearchResults({results,onSelect,onViewAll}){
  if(!results.query)return <p className="px-4 py-5 text-sm text-slate-500">Search merchants, categories, people, budgets or recurring rules.</p>;
  return <div className="max-h-[min(62dvh,520px)] overflow-y-auto overscroll-contain" aria-label="Workspace search results">
    <div role="status" className="border-b border-slate-100 px-4 py-2 text-xs text-slate-500">
      {results.total ? results.total+' matches across your workspace' : 'No matching records'}
    </div>
    {results.groups.map(group=><section key={group.name} className="border-b border-slate-100">
      <div className="flex items-center justify-between bg-slate-50 px-4 py-2 text-[11px] font-bold uppercase tracking-wide text-[#476080]">
        <span>{group.name}</span><span>{group.count}</span>
      </div>
      {group.rows.map((row,index)=><button type="button" key={group.name+'-'+String(row.id??index)+'-'+index}
        onClick={()=>onSelect(row.path)}
        className="flex min-h-12 w-full items-center gap-3 border-b border-slate-50 px-4 py-2.5 text-left transition hover:bg-blue-50 focus-visible:bg-blue-50">
        <Fi.FiSearch aria-hidden="true" size={15} className="shrink-0 text-[#0176D3]"/>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-bold text-slate-900">{row.title}</span>
          <span className="mt-0.5 block truncate text-[11px] text-slate-500">{row.kind} · {row.detail}</span>
        </span><Fi.FiChevronRight size={16} className="shrink-0 text-slate-400"/>
      </button>)}
      {group.count>group.rows.length&&<p className="px-4 py-1.5 text-[11px] text-slate-500">Showing {group.rows.length} of {group.count} matches</p>}
    </section>)}
    <button type="button" onClick={onViewAll}
      className="flex min-h-11 w-full items-center justify-between gap-2 bg-blue-50 px-4 py-3 text-left text-xs font-bold text-[#0176D3] hover:bg-blue-100">
      <span>View transactions matching “{results.query}” across all dates</span><Fi.FiArrowRight size={17}/>
    </button>
  </div>;
}

export default function Header(){
  const {state,actions}=useApp(),loc=useLocation(),navigate=useNavigate();
  const showView=loc.pathname==='/'||loc.pathname==='/reports';
  const [search,setSearch]=useState('');
  const [searchOpen,setSearchOpen]=useState(false);
  const [mobileSearchOpen,setMobileSearchOpen]=useState(false);
  const searchBoxRef=useRef(null),desktopInput=useRef(null),mobileInput=useRef(null);
  const matches=React.useMemo(()=>searchWorkspace(state,search),[
    search,state.transactions,state.people,state.recurringRules,state.budgets,state.categories
  ]);

  // A fresh page starts with an empty discovery query, unless Transactions is
  // already displaying a search passed by a bookmark or a navigation result.
  useEffect(()=>{
    setSearch(new URLSearchParams(loc.search).get('search')||'');
    setSearchOpen(false);
    setMobileSearchOpen(false);
  },[loc.pathname]);
  useEffect(()=>{
    if(['/transactions','/people','/recurring','/budgets','/categories'].includes(loc.pathname)){
      const value=new URLSearchParams(loc.search).get('search')||'';
      setSearch(previous=>previous===value?previous:value);
    }
  },[loc.pathname,loc.search]);

  useEffect(()=>{
    if(!searchOpen)return;
    const outside=e=>{if(!searchBoxRef.current?.contains(e.target))setSearchOpen(false)};
    document.addEventListener('pointerdown',outside);
    return()=>document.removeEventListener('pointerdown',outside);
  },[searchOpen]);

  useEffect(()=>{
    const openSearch=()=>{
      if(window.matchMedia('(max-width: 1279px)').matches)setMobileSearchOpen(true);
      else {setSearchOpen(true);desktopInput.current?.focus()}
    };
    window.addEventListener('expense:open-search',openSearch);
    return()=>window.removeEventListener('expense:open-search',openSearch);
  },[]);
  useEffect(()=>{
    if(!mobileSearchOpen)return;
    const previous=document.body.style.overflow;
    document.body.style.overflow='hidden';
    const keyboard=e=>{if(e.key==='Escape')setMobileSearchOpen(false)};
    document.addEventListener('keydown',keyboard);
    mobileInput.current?.focus();
    return()=>{document.body.style.overflow=previous;document.removeEventListener('keydown',keyboard)};
  },[mobileSearchOpen]);

  const searchUrl=(value,pathname='/transactions')=>{
    const params=new URLSearchParams();
    if(value.trim())params.set('search',value);
    return pathname+(params.toString()?'?'+params.toString():'');
  };
  const onInput=value=>{
    setSearch(value);setSearchOpen(true);
    // Typing in the desktop header while viewing Transactions is an instant
    // filter, exactly like the in-page search. Replace history, never spam it.
    if(['/transactions','/people','/recurring','/budgets','/categories'].includes(loc.pathname)){
      const target=searchUrl(value,loc.pathname);
      if(target!==loc.pathname+loc.search)navigate(target,{replace:true});
    }
  };
  const openResult=path=>{setSearchOpen(false);setMobileSearchOpen(false);navigate(path)};
  const viewAll=()=>{
    setSearchOpen(false);setMobileSearchOpen(false);
    navigate(searchUrl(search));
  };

  return <header className="bg-white/95 border-b border-slate-200 min-h-[72px] px-3 sm:px-4 md:px-6 flex items-center justify-between gap-2 sm:gap-4">
    <div className="flex items-center gap-3 flex-1 min-w-0">
      <button type="button" aria-label="Open menu" className="hidden p-2 text-slate-600" onClick={actions.toggleSidebar}><Fi.FiMenu/></button>
      <h1 className="hidden min-w-0 shrink-0 truncate text-base font-bold text-slate-900 md:block">{pages[loc.pathname]||'Expense Manager Pro'}</h1>
      <form ref={searchBoxRef} className="relative ml-2 hidden w-full max-w-md xl:ml-4 xl:block"
        onSubmit={e=>{e.preventDefault();viewAll()}}>
        <Fi.FiSearch className="pointer-events-none absolute left-4 top-3 text-slate-400"/>
        <input ref={desktopInput} type="search" value={search}
          onFocus={()=>setSearchOpen(true)} onChange={e=>onInput(e.target.value)}
          onKeyDown={e=>{if(e.key==='Escape')setSearchOpen(false)}}
          aria-label="Search entire app" aria-controls="workspace-search-results"
          aria-expanded={searchOpen&&Boolean(search.trim())}
          placeholder="Search across your app..." autoComplete="off"
          className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2.5 pl-11 pr-10 text-sm focus:border-blue-300 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-100"/>
        {search&&<button type="button" aria-label="Clear app search"
          onClick={()=>{onInput('');setSearchOpen(false)}} className="absolute right-2 top-1.5 grid h-8 w-8 place-items-center rounded-lg text-slate-400 hover:bg-slate-100"><Fi.FiX/></button>}
        {searchOpen&&search.trim()&&<div id="workspace-search-results"
          className="absolute left-0 right-0 top-[calc(100%+8px)] z-[85] overflow-hidden rounded-2xl border border-[#dce5f0] bg-white shadow-xl">
          <WorkspaceSearchResults results={matches} onSelect={openResult} onViewAll={viewAll}/>
        </div>}
      </form>
    </div>
    <div className="flex shrink-0 items-center gap-1.5 sm:gap-3 md:gap-4">
      <button type="button" aria-label="Search entire app" onClick={()=>setMobileSearchOpen(true)}
        className="xl:hidden grid h-11 w-11 place-items-center rounded-xl border border-slate-200 bg-white text-[#0176D3] hover:bg-blue-50 focus-visible:ring-2 focus-visible:ring-blue-300">
        <Fi.FiSearch size={21}/>
      </button>
      <GoogleAutoSyncStatus/><InstallAppButton/>
      <span className="hidden xl:flex gap-2 items-center text-slate-500 text-xs"><Fi.FiCalendar/>{new Date().toLocaleString('en-AU',{day:'numeric',month:'short',year:'numeric'})}</span>
      {showView&&<button type="button" onClick={actions.toggleView}
        aria-label={state.ui.currentView==='current'?'Switch to Future View':'Switch to Current View'}
        title={state.ui.currentView==='current'?'Future View':'Current View'}
        className="inline-flex min-h-10 min-w-10 items-center justify-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-2 sm:px-3 py-2 text-xs font-semibold text-blue-600">
        <Fi.FiEye/><span className="hidden xl:inline">{state.ui.currentView==='current'?'Future View':'Current View'}</span></button>}
      <div className="hidden sm:grid h-9 w-9 shrink-0 rounded-full bg-slate-700 text-white font-semibold text-xs place-items-center" title="Customer workspace">EM</div>
      <button type="button" onClick={()=>{clearSession();window.dispatchEvent(new Event('bpa:auth-changed'));}} title="Sign out" aria-label="Sign out of BPA" className="grid h-10 w-10 shrink-0 place-items-center rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"><Fi.FiLogOut/></button>
    </div>

    {mobileSearchOpen&&<div className="fixed inset-0 z-[95] bg-slate-950/50 backdrop-blur-[2px] flex items-start justify-center p-0 sm:p-4"
      onMouseDown={e=>{if(e.target===e.currentTarget)setMobileSearchOpen(false)}}>
      <section role="dialog" aria-modal="true" aria-label="Search entire app"
        className="flex max-h-[90dvh] w-full max-w-2xl flex-col overflow-hidden rounded-b-3xl bg-white shadow-2xl sm:rounded-3xl">
        <div className="flex shrink-0 items-center gap-2 border-b border-slate-100 p-4">
          <Fi.FiSearch size={20} className="shrink-0 text-[#0176D3]"/>
          <input ref={mobileInput} type="search" aria-label="Search workspace"
            value={search} onChange={e=>onInput(e.target.value)}
            onKeyDown={e=>{if(e.key==='Enter'){e.preventDefault();viewAll()}}}
            placeholder="Search transactions, people, budgets..."
            autoComplete="off"
            className="min-h-11 min-w-0 flex-1 bg-white text-base text-slate-900 outline-none"/>
          {search&&<button type="button" aria-label="Clear app search" onClick={()=>onInput('')}
            className="grid h-10 w-10 place-items-center rounded-xl text-slate-500 hover:bg-slate-100"><Fi.FiX size={19}/></button>}
          <button type="button" aria-label="Close app search" onClick={()=>setMobileSearchOpen(false)}
            className="grid h-10 w-10 place-items-center rounded-xl bg-slate-100 text-slate-700"><Fi.FiX size={20}/></button>
        </div>
        <WorkspaceSearchResults results={matches} onSelect={openResult} onViewAll={viewAll}/>
      </section>
    </div>}
  </header>;
}